/**
 * AlertBell.tsx
 *
 * Bell icon component for the Sidebar / Header area.
 * Displays an unread badge count and expands a dropdown panel
 * showing the most recent new alerts with quick action buttons.
 *
 * Polls for stats every 5 minutes and fetches the latest 10
 * new alerts when the dropdown opens.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Bell, Check, Clock, CheckCircle, Loader2, AlertTriangle, RefreshCw } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import alertService, {
  type IntelligenceAlertResponse,
  type AlertStats,
} from '@/services/AlertService'

// ====== Constants ======

const POLL_INTERVAL_MS = 300_000 // 5 minutes
const ALERTS_DROPDOWN_LIMIT = 10

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'var(--state-danger)',
  high: 'var(--state-warning)',
  medium: 'var(--accent)',
  low: 'var(--text-secondary)',
}

// ====== Helpers ======

function severityColor(severity: string): string {
  return SEVERITY_COLORS[severity] ?? 'var(--text-muted)'
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

// ====== Component ======

export const AlertBell: React.FC = () => {
  const [stats, setStats] = useState<AlertStats | null>(null)
  const [alerts, setAlerts] = useState<IntelligenceAlertResponse[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [statsError, setStatsError] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [actionError, setActionError] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // ------ Poll for stats ------
  const fetchStats = useCallback(async () => {
    try {
      const data = await alertService.getStats()
      setStats(data)
      setStatsError(false)
    } catch {
      setStatsError(true)
    }
  }, [])

  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchStats])

  // ------ Fetch alerts when dropdown opens ------
  const fetchAlerts = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await alertService.listAlerts({
        status: 'new',
        limit: ALERTS_DROPDOWN_LIMIT,
      })
      setAlerts(data.alerts)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchAlerts()
      fetchStats() // Refresh badge count when dropdown opens
    }
  }, [isOpen, fetchAlerts, fetchStats])

  // ------ Close on outside click ------
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ------ Actions ------
  const handleAcknowledge = async (id: string) => {
    setActionLoading(id)
    setActionError(false)
    try {
      await alertService.acknowledge(id)
      setAlerts((prev) => prev.filter((a) => a.id !== id))
      setStats((prev) =>
        prev ? { ...prev, total_new: Math.max(0, prev.total_new - 1) } : prev
      )
    } catch {
      setActionError(true)
    } finally {
      setActionLoading(null)
    }
  }

  const handleSnooze = async (id: string) => {
    setActionLoading(id)
    setActionError(false)
    try {
      await alertService.snooze(id, 1)
      setAlerts((prev) => prev.filter((a) => a.id !== id))
      setStats((prev) =>
        prev ? { ...prev, total_new: Math.max(0, prev.total_new - 1) } : prev
      )
    } catch {
      setActionError(true)
    } finally {
      setActionLoading(null)
    }
  }

  const handleResolve = async (id: string) => {
    setActionLoading(id)
    setActionError(false)
    try {
      await alertService.resolve(id)
      setAlerts((prev) => prev.filter((a) => a.id !== id))
      setStats((prev) =>
        prev ? { ...prev, total_new: Math.max(0, prev.total_new - 1) } : prev
      )
    } catch {
      setActionError(true)
    } finally {
      setActionLoading(null)
    }
  }

  const totalNew = stats?.total_new ?? 0

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors hover:bg-[var(--bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/40"
        aria-label={`Alerts${totalNew > 0 ? ` (${totalNew} new)` : ''}${statsError ? ' (count unavailable)' : ''}`}
        aria-expanded={isOpen}
      >
        <Bell className="w-5 h-5 text-[var(--text-secondary)]" />

        {/* Badge */}
        {totalNew > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full"
            style={{
              backgroundColor: 'var(--accent)',
              color: 'var(--bg-canvas)',
            }}
          >
            {totalNew > 99 ? '99+' : totalNew}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute right-0 mt-2 w-[380px] max-h-[480px] flex flex-col rounded-xl overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--shadow-lg)',
              zIndex: 'var(--z-dropdown)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 shrink-0"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <h3
                className="text-sm font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Alerts
              </h3>
              {totalNew > 0 && (
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: 'var(--accent-muted)',
                    color: 'var(--accent)',
                  }}
                >
                  {totalNew} new
                </span>
              )}
            </div>

            {/* Alert list */}
            <div className="flex-1 overflow-y-auto">
              {actionError && (
                <div role="alert" className="flex items-start gap-2 border-b border-[var(--state-danger)]/30 bg-[var(--state-danger)]/10 px-4 py-3 text-xs text-[var(--state-danger)]">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Ricald couldn’t update that alert. Nothing changed.</span>
                </div>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2
                    className="w-5 h-5 animate-spin"
                    style={{ color: 'var(--text-muted)' }}
                  />
                </div>
              ) : loadError ? (
                <div role="alert" className="flex flex-col items-center justify-center px-4 py-10 text-center">
                  <AlertTriangle className="mb-2 h-8 w-8 text-[var(--state-danger)]" />
                  <p className="text-sm font-medium text-[var(--text-primary)]">Couldn’t load alerts</p>
                  <button type="button" onClick={() => void fetchAlerts()} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[var(--accent)] hover:underline">
                    <RefreshCw className="h-3.5 w-3.5" /> Retry
                  </button>
                </div>
              ) : alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <Bell
                    className="w-8 h-8 mb-2"
                    style={{ color: 'var(--text-subtle)' }}
                  />
                  <p
                    className="text-sm"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    No new alerts
                  </p>
                </div>
              ) : (
                alerts.map((alert) => {
                  const isProcessing = actionLoading === alert.id

                  return (
                    <div
                      key={alert.id}
                      className="px-4 py-3 transition-colors hover:bg-[var(--bg-hover)]"
                      style={{ borderBottom: '1px solid var(--border-muted)' }}
                    >
                      {/* Title row */}
                      <div className="flex items-start gap-2.5">
                        {/* Severity dot */}
                        <span
                          className="mt-1.5 flex-shrink-0 w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: severityColor(alert.severity) }}
                          title={alert.severity}
                        />

                        <div className="flex-1 min-w-0">
                          <p
                            className="text-sm font-medium leading-snug truncate"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {alert.title}
                          </p>
                          <p
                            className="text-xs mt-0.5 line-clamp-2"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {alert.message}
                          </p>
                          <p
                            className="text-[11px] mt-1"
                            style={{ color: 'var(--text-muted)' }}
                          >
                            {timeAgo(alert.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1.5 mt-2 ml-5">
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          disabled={isProcessing}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors disabled:opacity-50"
                          style={{
                            color: 'var(--text-secondary)',
                            backgroundColor: 'var(--bg-elevated)',
                            border: '1px solid var(--border)',
                          }}
                          title="Acknowledge"
                        >
                          <Check className="w-3 h-3" />
                          Ack
                        </button>

                        <button
                          onClick={() => handleSnooze(alert.id)}
                          disabled={isProcessing}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors disabled:opacity-50"
                          style={{
                            color: 'var(--text-secondary)',
                            backgroundColor: 'var(--bg-elevated)',
                            border: '1px solid var(--border)',
                          }}
                          title="Snooze for 1 hour"
                        >
                          <Clock className="w-3 h-3" />
                          Snooze 1h
                        </button>

                        <button
                          onClick={() => handleResolve(alert.id)}
                          disabled={isProcessing}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors disabled:opacity-50"
                          style={{
                            color: 'var(--state-success)',
                            backgroundColor: 'var(--bg-elevated)',
                            border: '1px solid var(--border)',
                          }}
                          title="Resolve"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Resolve
                        </button>

                        {isProcessing && (
                          <Loader2
                            className="w-3.5 h-3.5 animate-spin ml-1"
                            style={{ color: 'var(--text-muted)' }}
                          />
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {/* Footer */}
            <div
              className="shrink-0 px-4 py-2.5 text-center"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <Link
                to="/app/alerts"
                onClick={() => setIsOpen(false)}
                className="text-xs font-medium transition-colors hover:underline"
                style={{ color: 'var(--accent)' }}
              >
                View All Alerts
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
